import type { DocumentLineInput } from '../types/documents.types';

export const VISUAL_DESIGNER_FEATURE = 'visual_designer';

export function isVisualDesignerLine(line: { metaJson?: any } | null | undefined): boolean {
  return String(line?.metaJson?.feature || '').trim() === VISUAL_DESIGNER_FEATURE;
}

function deepClone<T>(value: T): T {
  const sc = (globalThis as any)?.structuredClone as undefined | ((v: any) => any);
  if (typeof sc === 'function') return sc(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

export type FirstSurveyReference = {
  width_mm?: number | null;
  height_mm?: number | null;
  specs?: any;
};

function clearFinalEditableMeasures(specs: Record<string, any>): void {
  specs.width_mm = null;
  specs.height_mm = null;
  const options = specs.options && typeof specs.options === 'object' ? { ...specs.options } : {};
  options.cill_option = null;
  options.cill_included = null;
  options.cill_required = null;
  options.cill_type = null;
  options.cill_height_mm = null;
  options.ext_top_mm = null;
  options.ext_bottom_mm = null;
  options.ext_left_mm = null;
  options.ext_right_mm = null;
  options.bay_tangent_width_mm = null;
  options.bay_legacy_width_mm = null;
  options.bay_projection_depth_mm = null;
  options.bay_facet_widths_mm = null;
  options.bay_joint_angles_deg = null;
  specs.options = options;
}

/**
 * Clone First Survey item into Final Survey.
 * Drawings / templatePayload stay intact for persistence.
 * Editable measure fields (width/height, bay tangent/facets, cill, add-on extenders) start empty;
 * originals live under survey.first_survey_reference for the reference panel.
 */
export function cloneLineForFinalSurvey(line: DocumentLineInput): DocumentLineInput {
  const cloned = deepClone(line);
  const meta = cloned.metaJson && typeof cloned.metaJson === 'object' ? { ...cloned.metaJson } : {};
  const specs = meta.specs && typeof meta.specs === 'object' ? { ...meta.specs } : {};
  meta.survey = {
    source: 'final',
    first_survey_reference: {
      width_mm: specs.width_mm ?? null,
      height_mm: specs.height_mm ?? null,
      specs: deepClone(specs),
    },
  };
  clearFinalEditableMeasures(specs);
  meta.specs = specs;
  cloned.metaJson = meta;
  return cloned;
}

/** Full clone for a new 1:1 quote version (dims kept). */
export function cloneLineForBoundVersion(line: DocumentLineInput): DocumentLineInput {
  return deepClone(line);
}

export function getFirstSurveyReference(meta: any): FirstSurveyReference | null {
  const ref = meta?.survey?.first_survey_reference;
  if (!ref || typeof ref !== 'object') return null;
  return ref as FirstSurveyReference;
}

export function isFinalSurveyMeta(meta: any): boolean {
  return String(meta?.survey?.source || '').trim().toLowerCase() === 'final';
}

/** Final Survey edit form should not fall back to drawing payload dims/cill/addons. */
export function finalSurveyUsesEmptyMeasureFields(meta: any): boolean {
  return isFinalSurveyMeta(meta);
}

export function formatFirstSurveyReferenceSummary(ref: FirstSurveyReference | null | undefined): string {
  if (!ref || typeof ref !== 'object') return 'No stored First Survey measurements.';
  const specs = ref.specs && typeof ref.specs === 'object' ? ref.specs : {};
  const options = specs.options && typeof specs.options === 'object' ? specs.options : {};
  const tangent = options.bay_tangent_width_mm;
  const isBay = (Array.isArray(options.bay_facet_widths_mm) && options.bay_facet_widths_mm.length > 0)
    || options.bay_projection_depth_mm != null
    || (Array.isArray(options.bay_joint_angles_deg) && options.bay_joint_angles_deg.length > 0);
  const w = isBay ? (tangent ?? ref.width_mm ?? specs.width_mm) : (ref.width_mm ?? specs.width_mm);
  const h = ref.height_mm ?? specs.height_mm;
  const facetWidths = Array.isArray(options.bay_facet_widths_mm)
    ? (options.bay_facet_widths_mm as unknown[])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n))
    : [];
  const facets = facetWidths.length
    ? `Facets: ${facetWidths.map((n) => `${Math.round(n)}mm`).join(', ')}`
    : null;
  const cillOpt = options.cill_option;
  const cill =
    cillOpt === '150' ? 'Cill: 150mm'
    : cillOpt === '180' ? 'Cill: 180mm'
    : cillOpt === 'stub' ? 'Cill: Stub'
    : cillOpt === 'none' ? 'Cill: None'
    : options.cill_height_mm != null ? `Cill: ${options.cill_height_mm}mm`
    : null;
  const extParts = [
    options.ext_top_mm != null && options.ext_top_mm !== '' ? `T${options.ext_top_mm}` : null,
    options.ext_bottom_mm != null && options.ext_bottom_mm !== '' ? `B${options.ext_bottom_mm}` : null,
    options.ext_left_mm != null && options.ext_left_mm !== '' ? `L${options.ext_left_mm}` : null,
    options.ext_right_mm != null && options.ext_right_mm !== '' ? `R${options.ext_right_mm}` : null,
  ].filter(Boolean);
  const addOns = extParts.length > 0 ? `Add-ons: ${extParts.join('/')}` : null;
  const specBits = [
    w != null ? (isBay ? `Tangent W: ${w}mm` : `W: ${w}mm`) : null,
    h != null ? `H: ${h}mm` : null,
    facets,
    specs.glass_type ? `Glass: ${specs.glass_type}` : null,
    specs.variant ? `Profile: ${specs.variant}` : null,
    specs.frame_color ? `Colour: ${specs.frame_color}` : null,
    cill,
    addOns,
  ].filter(Boolean);
  return specBits.join(' • ') || 'No stored First Survey measurements.';
}
